import React, { useEffect, useRef, useState } from "react";
import { FiX, FiDownload, FiTrash2, FiLoader, FiFileText, FiInfo, FiCopy, FiCheck, FiClipboard, FiUploadCloud } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds, formatSize } from "./audioShared";

const ACCEPT = ".pdf,.docx,.pptx,.odt,.html,.htm,.txt,.md,.csv";
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_CHARS = 600000;
const MIN_CHARS = 200;

const LENGTHS = [
  { key: "short", label: "Short", hint: "About 90 words - the main point at a glance." },
  { key: "medium", label: "Medium", hint: "About 220 words - the main points with some detail." },
  { key: "long", label: "Detailed", hint: "About 450 words - a thorough summary." },
];
const STYLES = [
  { key: "paragraph", label: "Paragraphs" },
  { key: "bullets", label: "Bullet points" },
];
const LANGUAGES = [
  ["same", "Same as the document"], ["en", "English"], ["ur", "Urdu"], ["hi", "Hindi"], ["pa", "Punjabi"], ["ar", "Arabic"],
  ["fa", "Persian"], ["tr", "Turkish"], ["bn", "Bengali"], ["fr", "French"], ["de", "German"], ["es", "Spanish"],
  ["pt", "Portuguese"], ["it", "Italian"], ["ru", "Russian"], ["zh-CN", "Chinese"], ["ja", "Japanese"], ["ko", "Korean"], ["id", "Indonesian"],
];
const selectClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";

function download(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}

function CopyButton({ getText, label }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => { if (await copyText(getText())) { setDone(true); setTimeout(() => setDone(false), 1800); } }}
      className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors"
    >
      {done ? <><FiCheck className="text-emerald-400" /> Copied</> : <><FiCopy /> {label}</>}
    </button>
  );
}

// The summary text: "- " lines are bullets, anything else is a paragraph.
function SummaryText({ text }) {
  const blocks = text.split("\n\n").filter(Boolean);
  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        return lines.every((l) => l.startsWith("- ")) ? (
          <ul key={i} className="list-disc ps-5 space-y-2 marker:text-accent-light">
            {lines.map((l, j) => <li key={j} className="text-gray-100 leading-relaxed">{l.slice(2)}</li>)}
          </ul>
        ) : (
          <p key={i} className="text-gray-100 leading-relaxed whitespace-pre-line">{block}</p>
        );
      })}
    </div>
  );
}

// AI Document Summarization: a document (or pasted text) in, a short summary with key points and keywords out.
// The work runs on the server in the background and this page polls for the result.
export default function DocumentSummarizerTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("summary");
  const [source, setSource] = useState("file");   // "file" | "text"
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [length, setLength] = useState("medium");
  const [style, setStyle] = useState("paragraph");
  const [language, setLanguage] = useState("same");
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const inputRef = useRef(null);

  const hasInput = source === "file" ? !!file : text.trim().length >= MIN_CHARS;
  const canSubmit = hasInput && !submitting && !running;

  // Show the newest finished summary automatically.
  useEffect(() => {
    if (latest && latest.status === "completed" && latest.has_transcript) setViewId(latest.id);
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => {
      if (alive) setDetail(ok ? data : null);
    });
    return () => { alive = false; };
  }, [viewId, clientId]);

  const pickFile = (picked) => {
    setFieldError("");
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) return setFieldError(`This file is larger than ${MAX_FILE_BYTES / 1024 / 1024} MB.`);
    setFile(picked);
  };

  const submit = async () => {
    setError("");
    setFieldError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "summary");
    form.append("length", length);
    form.append("style", style);
    form.append("language", language);
    if (source === "file") form.append("original", file);
    else form.append("text", text);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.original || data?.text) return setFieldError(data.original || data.text);
      return setError(errorMessage(data));
    }
    setViewId(null);
    add(data);
  };

  const extra = detail?.segments && !Array.isArray(detail.segments) ? detail.segments : {};
  const stats = extra.stats || {};
  const keyPoints = extra.key_points || [];
  const bullets = detail && detail.options && (() => { try { return JSON.parse(detail.options).style === "bullets"; } catch { return false; } })();
  const arabicShare = detail ? (detail.transcript.match(/[؀-ۿ]/g) || []).length / Math.max(1, detail.transcript.length) : 0;
  const rtl = arabicShare > 0.3;
  const everything = (() => {
    if (!detail) return "";
    const parts = [detail.transcript];
    if (!bullets && keyPoints.length) parts.push("Key points:\n" + keyPoints.map((p) => `- ${p}`).join("\n"));
    if (extra.keywords?.length) parts.push("Keywords: " + extra.keywords.join(", "));
    return parts.join("\n\n");
  })();
  const baseName = (detail?.original_name || "summary").replace(/\.[^.]+$/, "");

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Document Summarization</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Turn a long report, contract, article, paper or presentation into a short summary with the key points. Upload a document
        or paste the text, choose how long the summary should be, and read it in your own language.
      </p>

      <div className="flex justify-center mb-6">
        <div className="inline-flex rounded-full bg-white/5 border border-navy-border p-1">
          {[["file", "Upload a document", FiUploadCloud], ["text", "Paste text", FiClipboard]].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => { setSource(key); setFieldError(""); }}
              className={`px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${source === key ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "text-gray-300 hover:text-white"}`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </div>

      {source === "file" ? (
        <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
          {!file ? (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-12 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
            >
              <FiFileText className="mx-auto text-accent-light" size={32} />
              <p className="mt-3 text-sm text-gray-200">Click to choose a document, or drop it here</p>
              <p className="text-xs text-gray-500 mt-1">PDF, Word (.docx), PowerPoint (.pptx), OpenDocument, HTML, TXT - up to 25 MB</p>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-xl bg-navy border border-navy-border p-4">
              <div className="flex items-center gap-3 min-w-0">
                <FiFileText className="text-accent-light shrink-0" size={22} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatSize(file.size)}</p>
                </div>
              </div>
              <button type="button" onClick={() => { setFile(null); setFieldError(""); }} aria-label="Remove document" className="text-gray-400 hover:text-red-400 shrink-0"><FiX size={18} /></button>
            </div>
          )}
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
          <p className="text-xs text-gray-500 mt-3">Scanned PDFs (pictures of pages) have no text to read. Use a text-based PDF, or paste the text.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
          <div className="flex items-center justify-between mb-3">
            <label htmlFor="summary-text" className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your text</label>
            <span className="text-xs text-gray-500">{text.length.toLocaleString()} characters · {text.trim() ? text.trim().split(/\s+/).length.toLocaleString() : 0} words</span>
          </div>
          <textarea
            id="summary-text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Paste the article, report or notes you want summarized (at least a few paragraphs)..."
            className="w-full min-h-[240px] px-5 py-4 rounded-xl bg-navy border border-navy-border text-white text-base leading-relaxed focus:outline-none focus:border-accent-light resize-y"
          />
          {text.trim().length > 0 && text.trim().length < MIN_CHARS && <p className="text-xs text-amber-300 mt-2">Add a little more text - at least {MIN_CHARS} characters are needed.</p>}
        </div>
      )}
      {fieldError && <p className="text-sm text-amber-300 mt-3">{fieldError}</p>}

      <div className="rounded-2xl bg-white/5 border border-navy-border p-6 mt-6 grid md:grid-cols-3 gap-6">
        <div>
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3">Length</span>
          <div className="flex flex-wrap gap-2">
            {LENGTHS.map((l) => (
              <button key={l.key} type="button" title={l.hint} onClick={() => setLength(l.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${length === l.key ? "bg-accent-light text-navy" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"}`}>
                {l.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">{LENGTHS.find((l) => l.key === length)?.hint}</p>
        </div>
        <div>
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3">Format</span>
          <div className="flex flex-wrap gap-2">
            {STYLES.map((s) => (
              <button key={s.key} type="button" onClick={() => setStyle(s.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${style === s.key ? "bg-accent-light text-navy" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label htmlFor="summary-language" className="text-xs uppercase tracking-widest text-white/50 font-semibold block mb-3">Summary language</label>
          <select id="summary-language" className={selectClass} value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGUAGES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>
      </div>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Uploading...</> : running ? <><FiLoader className="animate-spin" /> Summarizing...</> : <><FiFileText /> Summarize</>}
      </button>
      {!canSubmit && !running && !submitting && (
        <p className="text-xs text-gray-500 text-center mt-2">{source === "file" ? "Add a document first" : "Paste some text first"}</p>
      )}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div>
            <p className="font-semibold">
              Reading and summarizing... {formatSeconds(elapsed)}
              {running.progress && <span className="text-gray-400 font-normal"> · part {running.progress.replace("/", " of ")}</span>}
            </p>
            <p className="text-sm text-gray-400">This usually takes a few seconds; a very long document or a translation can take a minute.</p>
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The document could not be summarized. Please try again."}</span>
        </div>
      )}

      {detail && detail.status === "completed" && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Summary</p>
              <p className="font-semibold truncate">{detail.original_name}</p>
            </div>
            {detail.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">{detail.engine}</span>}
          </div>

          {stats.words_in > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-5 text-center">
              <div className="rounded-xl bg-navy border border-navy-border py-3"><p className="text-xl font-extrabold text-accent-light">{stats.words_in.toLocaleString()}</p><p className="text-[11px] text-gray-400">words in the document</p></div>
              <div className="rounded-xl bg-navy border border-navy-border py-3"><p className="text-xl font-extrabold text-accent-light">{stats.words_out.toLocaleString()}</p><p className="text-[11px] text-gray-400">words in the summary ({stats.reduced_to_percent}%)</p></div>
              <div className="rounded-xl bg-navy border border-navy-border py-3"><p className="text-xl font-extrabold text-accent-light">~{stats.minutes_saved} min</p><p className="text-[11px] text-gray-400">reading time saved</p></div>
            </div>
          )}

          {stats.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-5">{stats.notice}</p>}

          <div className="rounded-xl bg-navy border border-navy-border p-5" dir={rtl ? "rtl" : "ltr"}>
            <SummaryText text={detail.transcript} />
          </div>

          {!bullets && keyPoints.length > 0 && (
            <div className="mt-5" dir={rtl ? "rtl" : "ltr"}>
              <p className="text-sm font-semibold mb-2">Key points</p>
              <ul className="list-disc ps-5 space-y-1.5 marker:text-accent-light">
                {keyPoints.map((point, i) => <li key={i} className="text-gray-300 text-sm leading-relaxed">{point}</li>)}
              </ul>
            </div>
          )}

          {extra.keywords?.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold mb-2">Keywords</p>
              <div className="flex flex-wrap gap-2">
                {extra.keywords.map((k) => <span key={k} className="px-3 py-1 rounded-full bg-accent/10 border border-accent-light/30 text-accent-light text-xs">{k}</span>)}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            <CopyButton getText={() => everything} label="Copy summary" />
            <button type="button" onClick={() => download(`${baseName}-summary.txt`, everything)} className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors">
              <FiDownload /> Download .txt
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            {detail.engine && detail.engine.startsWith("Claude")
              ? "Written by an AI model from the document. Check important details against the original."
              : "The most important sentences were selected from the document (not rewritten), so every line is the document's own wording. For a rewritten summary, ask the site owner to enable the Claude option."}
          </p>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your summaries</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your summaries will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border p-4 ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                <p className="text-sm text-gray-200 truncate">{job.original_name || "Document"}</p>
                <p className="text-xs text-gray-500 mb-3">{new Date(job.created_at).toLocaleString()} · {job.status === "completed" ? "Ready" : job.status === "failed" ? "Failed" : "Working..."}</p>
                <div className="flex items-center justify-between gap-3">
                  {job.status === "completed" && job.has_transcript ? (
                    <button type="button" onClick={() => setViewId(job.id)} className="text-sm text-accent-light hover:underline">View summary</button>
                  ) : job.status === "failed" ? <p className="text-xs text-red-300">{job.error_message}</p> : <span />}
                  {job.status !== "processing" && job.status !== "queued" && (
                    <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
